import { useMemo } from 'react';
import { ActorLogicFrom, ActorRefFrom, fromPromise } from 'xstate';
import alloy from '@alloyidentity/web-sdk';
import { isLeft } from 'fp-ts/lib/Either';
import {
  FraudAlloyInitResponse,
  JourneyApplicationStatus,
} from '@ads-bread/shared/bread/codecs';
import { APPLICATION_STATUS_CODES } from '@ads-bread/shared/bread/util';
import {
  getFraudAlloy,
  getFraudAlloyKYC,
} from '../../../../lib/handlers/get-fraud-alloy';
import {
  pollFraudAlloyJourneyStatusKYC,
  pollFraudAlloyJourneyStatus,
} from '../../../../lib/handlers/get-fraud-alloy-journey-status';
import { createLogger } from '../../../../lib/logger';
import { UnauthorizedError, useFetch } from '../../../../lib/hooks/apiFetch';
import { useMerchantID } from '../../../../components/XPropsContext';
import { useAppConfig } from '../../../../components/AppConfigContext';
import { useAuthentication } from '../../../../components/AuthenticationMachineContext';
import { useMerchantConfig } from '../../../../components/MerchantContext';
import { shouldUseMockProvider } from '../../../../lib/mirage/utils';
import { fraudAlloyMachine } from './fraudAlloyMachine';
import {
  InitializingFraudAlloyResult,
  PollIDUploadJourneyStatusKYCResult,
  PollIDUploadJourneyStatusResult,
  StartIDUploadJourneyResult,
} from './types/actors';
import { initAlloyLib, mockAlloy, waitForIDUploadJourneyResult } from './utils';
import { assignFraudAlloyInitResponse } from './assigns';
import { emitFraudAlloyMachineReady } from './actions';

const logger = createLogger({
  name: 'checkout.fraud.alloy',
});

export type FraudAlloyMachineActorRef = ActorRefFrom<typeof fraudAlloyMachine>;

export type FraudAlloyMachineActorRefLogic = ActorLogicFrom<
  typeof fraudAlloyMachine
>;

export const useFraudAlloyMachineLogic = (): FraudAlloyMachineActorRefLogic => {
  const apiFetch = useFetch();
  const { logoutUnauthorized } = useAuthentication();
  const {
    environment,
    fraudAlloyBaseAPIKey,
    fraudAlloyBaseJourneyToken,
    tenant,
  } = useAppConfig();
  const { checkoutPrimaryColor, checkoutSecondaryColor } = useMerchantConfig();

  const { merchantID } = useMerchantID();

  const fraudAlloyMachineLogic = useMemo(() => {
    return fraudAlloyMachine.provide({
      guards: {
        isKYCAlloy: (_, params) => {
          return (
            params.enableKYCAlloyPhase2 &&
            params.applicationStatusCodes?.some(
              (statusCode) =>
                statusCode ===
                APPLICATION_STATUS_CODES.KYCIncompleteNeedsMoreInfo
            )
          );
        },
        isFraudJourneyStatusSuccess: (_, params): boolean => {
          return (
            params.journeyApplicationStatusRes?.result
              ?.journeyApplicationStatus === JourneyApplicationStatus.Approved
          );
        },
        isFraudJourneyStatusPending: (_, params): boolean => {
          return (
            params.journeyApplicationStatusRes?.result
              ?.journeyApplicationStatus === JourneyApplicationStatus.Pending
          );
        },
        isFraudJourneyStatusKYCSuccess: (_, params): boolean => {
          return (
            params.journeyApplicationStatusRes?.result
              ?.journey_application_status === JourneyApplicationStatus.Approved
          );
        },
        isFraudJourneyStatusKYCPending: (_, params): boolean => {
          return (
            params.journeyApplicationStatusRes?.result
              ?.journey_application_status === JourneyApplicationStatus.Pending
          );
        },
      },
      actions: {
        assignFraudAlloyInitResponse,
        emitFraudAlloyMachineReady,
        logStartIDUploadJourneyFailure: (_, params) => {
          logger.error(
            { ...params, merchantID },
            'FraudAlloyMachine: Start ID Upload Journey failed'
          );
        },
        logPollJourneyStatusFailure: (_, params) => {
          logger.error(
            { ...params, merchantID },
            'FraudAlloyMachine: Poll ID Upload Journey Status failed'
          );
        },
        logError: (_, params) => {
          logger.error(
            { err: params.error, merchantID },
            `Error in FraudAlloyMachine: ${params.type}`
          );
        },
      },
      actors: {
        initializeFraudAlloy: fromPromise<InitializingFraudAlloyResult>(
          async () => {
            try {
              const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

              if (tenant !== 'ADS') {
                return { fraudAlloyRes: undefined };
              }

              const alloyInit = await alloyLib.init({
                key: fraudAlloyBaseAPIKey,
                production: environment === 'production',
                journeyToken: fraudAlloyBaseJourneyToken,
              });

              const decoded = FraudAlloyInitResponse.decode(alloyInit);

              if (isLeft(decoded)) {
                logger.warn(
                  'Unexpected response from Alloy init. Continuing without FraudAlloy',
                  {
                    cause: alloyInit,
                  }
                );
                return { fraudAlloyRes: undefined };
              }

              const fraudAlloyRes: FraudAlloyInitResponse = decoded.right;

              return { fraudAlloyRes };
            } catch (error) {
              throw { error };
            }
          }
        ),
        startIDUploadJourney: fromPromise(async ({ input }) => {
          try {
            const res = await getFraudAlloy(input.applicationId, apiFetch);

            if (res.error) {
              if (res.error instanceof UnauthorizedError) {
                logoutUnauthorized();
              }
              throw res.error;
            }

            const {
              journey_token: journeyToken,
              journey_application_token: journeyApplicationToken,
              journey_sdk_key: sdkKey,
            } = res.result;

            const alloyLib = await initAlloyLib({
              journeyToken,
              journeyApplicationToken,
              journeySdkKey: sdkKey,
              isProduction: environment === 'production',
              primaryColor: checkoutPrimaryColor || '',
              secondaryColor: checkoutSecondaryColor || '',
            });

            return await waitForIDUploadJourneyResult({
              alloyLib,
              applicationId: input.applicationId,
              applicationStatusCodes: input.applicationStatusCodes,
              domID: input.domID,
              onJourneyData: (data) => {
                logger.info(
                  { data, merchantID },
                  'FraudAlloyMachine: Received callback from Alloy ID Upload Journey'
                );
              },
            });
          } catch (error) {
            throw { error };
          }
        }),
        startIDUploadJourneyKYC: fromPromise(async ({ input }) => {
          try {
            const res = await getFraudAlloyKYC(input.applicationId, apiFetch);

            if (res.error) {
              if (res.error instanceof UnauthorizedError) {
                logoutUnauthorized();
              }
              throw res.error;
            }

            const {
              journey_token: journeyToken,
              journey_application_token: journeyApplicationToken,
              journey_sdk_key: sdkKey,
            } = res.result;

            const alloyLib = shouldUseMockProvider() ? mockAlloy : alloy;

            await alloyLib.init({
              key: sdkKey,
              production: environment === 'production',
              journeyApplicationToken: journeyApplicationToken,
              journeyToken: journeyToken,
              /**
               * This flag is for multi applicant flows. If set to true we would need to
               * init alloy with the Entity ID of the applicant so that only their portion
               * would be displayed. As of 07/2024 we do not support multi applicant flows.
               */
              isSingleEntity: false,
              showHeader: true,
              apiUrl: 'https://docv.alloy.co/',
              appUrl: 'https://alloysdk.alloy.co',
              color: {
                primary: checkoutPrimaryColor || '',
                secondary: checkoutSecondaryColor || '',
              },
            });

            return await waitForIDUploadJourneyResult({
              alloyLib,
              applicationId: input.applicationId,
              applicationStatusCodes: input.applicationStatusCodes,
              domID: input.domID,
              onJourneyData: (data) => {
                logger.info(
                  { data, merchantID },
                  'FraudAlloyMachine: Received callback from Alloy ID Upload Journey'
                );
              },
            });
          } catch (error) {
            throw { error };
          }
        }),
        pollIDUploadJourneyStatus: fromPromise<
          PollIDUploadJourneyStatusResult,
          StartIDUploadJourneyResult
        >(async ({ input }) => {
          const applicationId = input.applicationId;
          if (!applicationId) {
            throw new Error(
              'Missing applicationId for pollIDUploadJourneyStatus'
            );
          }
          const journeyApplicationStatusRes = await pollFraudAlloyJourneyStatus(
            applicationId,
            apiFetch
          );

          return { journeyApplicationStatusRes };
        }),
        pollIDUploadJourneyStatusKYC: fromPromise<
          PollIDUploadJourneyStatusKYCResult,
          StartIDUploadJourneyResult
        >(async ({ input }) => {
          const applicationId = input.applicationId;
          const journeyApplicationStatusRes =
            await pollFraudAlloyJourneyStatusKYC(applicationId, apiFetch);

          return { journeyApplicationStatusRes };
        }),
      },
    });
  }, []);

  return fraudAlloyMachineLogic;
};
